import { parseCssUnit } from '@opten/number/parse-css-unit';
import { getCss } from '@opten/dom/get-css';
import { addClass } from '@opten/dom/add-class';
import { removeClass } from '@opten/dom/remove-class';
import { getSiblings } from '@opten/dom/get-siblings';

export interface StickToParentOptions {
	offsetTop?: number;
	fill?: boolean;
	withPadding?: boolean;
	withPaddingParent?: boolean;
	enableForMobile?: boolean;
	mobileWidth?: number;
	offsetTopMobile?: number;
	stickyClass?: string;
}

export class StickToParent {
	private options: StickToParentOptions = {
		offsetTop: 0,
		fill: false,
		withPadding: false,
		enableForMobile: false,
		mobileWidth: 1023,
		stickyClass: 'is-sticky',
	};
	private parentElement: HTMLElement;
	private initialHeight: number;
	private lastScroll: number;
	private lastOffsetted: number;

	constructor(private element: HTMLElement, options?: StickToParentOptions) {
		this.options = {
			...this.options,
			...options,
		};
		this.options.offsetTopMobile = this.options.offsetTopMobile
			? this.options.offsetTopMobile
			: this.options.offsetTop;
		this.parentElement = this.element.parentElement;
		this.initialHeight = this.getOuterHeight(this.element);
		if (this.options.withPaddingParent) {
			this.parentElement.style.paddingTop = this.initialHeight + 'px';
		}
		this.lastOffsetted = 0;
		window.addEventListener('scroll', () => this.scroll());
		window.addEventListener('touchmove', () => this.scroll());
		window.addEventListener('resize', () => this.scroll());
		setTimeout(() => this.scroll(), 0);
	}

	private scroll() {
		if (
			!this.options.enableForMobile &&
			window.outerWidth <= this.options.mobileWidth
		) {
			return;
		}
		const padding = this.options.withPadding
			? parseCssUnit(getCss(this.element, 'padding-top'))
			: 0;
		const offsetTop =
			window.outerWidth <= this.options.mobileWidth
				? this.options.offsetTopMobile
				: this.options.offsetTop;
		const elementTop =
			this.parentElement.getBoundingClientRect().top - offsetTop + padding;
		const elementHeight = this.options.fill
			? this.initialHeight
			: this.element.offsetHeight;
		const scroll = window.pageYOffset;
		const winHeight = window.outerHeight;
		const parentHeight = this.options.fill
			? this.getSiblingsMaxHeight()
			: this.parentElement.clientHeight;

		const isFixed = scroll > elementTop;
		const isBottom =
			scroll + offsetTop - padding + elementHeight >=
			this.parentElement.offsetTop +
				this.parentElement.offsetHeight -
				this.lastOffsetted;

		let height = this.initialHeight;
		let marginTop = 0;

		if (isBottom) {
			marginTop = parentHeight - elementHeight;
		} else if (isFixed) {
			marginTop = scroll - this.parentElement.offsetTop + offsetTop - padding;

			if (elementHeight + offsetTop > winHeight && this.options.fill) {
				if (this.lastScroll) {
					const diff = elementHeight + offsetTop - winHeight;
					const delta = this.lastScroll - scroll;

					if (delta > 0) {
						if (this.lastOffsetted + delta < 0) {
							this.lastOffsetted += delta;
						}
						marginTop += this.lastOffsetted;
					} else {
						if (Math.abs(this.lastOffsetted + delta) < Math.abs(diff)) {
							this.lastOffsetted += delta;
						}
						marginTop += this.lastOffsetted;
					}
				}
				this.lastScroll = scroll;
			}

			if (this.options.fill) {
				height = this.parentElement.clientHeight - marginTop;
			}
		} else {
			this.lastOffsetted = 0;
			if (this.options.fill) {
				height = parentHeight;
			}
		}

		if (marginTop > 0 && marginTop !== parentHeight - height) {
			addClass(this.element, this.options.stickyClass);
			this.element.style.marginTop = marginTop + 'px';
		} else {
			removeClass(this.element, this.options.stickyClass);
			this.element.style.marginTop = '0';
		}

		if (this.options.fill) {
			this.element.style.height = height + 'px';
		}
	}

	private getSiblingsMaxHeight(): number {
		return Math.max.apply(
			this,
			getSiblings(this.element).map((i, el) => {
				return i.clientHeight;
			})
		);
	}

	private getOuterHeight(el: HTMLElement): number {
		let height = el.offsetHeight;
		const style = getComputedStyle(el);

		height += parseInt(style.marginTop, 10) + parseInt(style.marginBottom, 10);
		return height;
	}
}

export function stickToParent(
	element: HTMLElement,
	options?: StickToParentOptions
): StickToParent {
	return new StickToParent(element, options);
}
